import { AppConfigs, AppCriteriaField, AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';
import React, { useEffect, useState } from 'react';
import { Button, Checkbox, FormControl, FormControlLabel, IconButton, MenuItem, Select } from '@mui/material';
import Slider from '../../../slider/Slider';
import { TriangleSlider } from '../../../slider';
import { playDragSound } from '../../../slider/HeartSlider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DraggableDiv from '../../../draggableDiv/DraggableDiv';
import NameWithIcon from '../NameWithIcon/NameWithIcon';

type SelectItem = {
    name: string;
    value: number | string;
    icon: string;
    link: string;
};

type ItemValues = {
    [p: string]: {
        isChecked: boolean;
        value: string;
    }
};

function toggleItemChecked(
    item: SelectItem,
    checked: boolean,
    setItemValues: (value: ((prevState: ItemValues) => ItemValues) | ItemValues) => void,
    onTradeoffValueChange: (value: any, id: string, key: string) => void,
    id: string,
    initialValues: { [p: string]: string },
) {
    setItemValues(items => (
        {
            ...items,
            [item.name]: {
                value: checked ? '50' : '-1',
                isChecked: checked,
            },
        }
    ));

    if (!checked) {
        onTradeoffValueChange('-1', id, item.name);
    } else {
        let defaultValue = initialValues[item.name];

        if (defaultValue === '-1') {
            defaultValue = '50';
        }

        onTradeoffValueChange(defaultValue, id, item.name);
    }
}

function renderSelectItems(
    items: SelectItem[],
    hideImportance: boolean,
    itemValues: ItemValues,
    setItemValues: (value: ((prevState: ItemValues) => ItemValues) | ItemValues) => void,
    onTradeoffValueChange: (value: any, id: string, key: string) => void,
    id: string,
    initialValues: { [p: string]: string },
    disabled: boolean,
    highlightedOptionNames: string[],
    expandedOptionNames: Set<string>,
    onExpandOption: (item: SelectItem) => void,
) {
    return (
        <>
            {items.map((item, index) => {
                const itemValue = +(itemValues[item.name]?.value ?? 0);
                const isChecked = !!itemValues[item.name]?.isChecked;
                const mentioned = highlightedOptionNames.includes(item.name);
                const revealed = mentioned || expandedOptionNames.has(item.name);

                return (
                    <div
                        key={item.name + index}
                        className={`multi-select-settings-item ${mentioned ? 'option-highlighted' : ''}`}
                        style={{ cursor: revealed ? 'default' : 'pointer' }}
                        onClick={() => {
                            if (!revealed) {
                                onExpandOption(item);
                            }
                        }}
                    >
                        <div style={{
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', minHeight: 40, width: '100%',
                        }}>
                            <span
                                aria-hidden="true"
                                style={{
                                    flexGrow: revealed ? 0 : 1,
                                    flexShrink: 1,
                                    flexBasis: 0,
                                    minWidth: 0,
                                    transition: 'flex-grow 550ms cubic-bezier(.2,.8,.2,1)',
                                }}
                            />

                            {revealed ? (
                                <div className="custom-slider-label truncate" style={{ flex: '0 0 auto' }}>
                                    <FormControlLabel
                                        disabled={disabled}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                sx={{
                                                    color: 'var(--app-primary, #2878d4)',
                                                    '&.Mui-checked': {
                                                        color: 'var(--app-primary, #2878d4)',
                                                    },
                                                }}
                                            />
                                        }
                                        label={<NameWithIcon icon={item.icon} name={item.name} link={item.link}/>}
                                        checked={isChecked}
                                        onChange={(_, checked) => toggleItemChecked(item, checked, setItemValues, onTradeoffValueChange, id, initialValues)}
                                    />
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => onExpandOption(item)}
                                    style={{
                                        flex: '0 0 auto',
                                        height: 36,
                                        border: 0,
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: '#1e2f46',
                                        letterSpacing: '-0.01em',
                                        transition: 'font-size 450ms ease-out, color 450ms ease-out',
                                    }}
                                >
                                    <NameWithIcon icon={item.icon} name={item.name} link={item.link}/>
                                </button>
                            )}

                            <span
                                aria-hidden="true"
                                style={{
                                    flexGrow: revealed ? 0 : 1,
                                    flexShrink: 1,
                                    flexBasis: 0,
                                    minWidth: 0,
                                    transition: 'flex-grow 550ms cubic-bezier(.2,.8,.2,1)',
                                }}
                            />

                            <div
                                className="custom-slider"
                                style={{
                                    position: 'relative',
                                    flex: revealed ? '0 0 50%' : '0 0 0px',
                                    width: revealed ? '50%' : 0,
                                    marginLeft: revealed ? 'auto' : 0,
                                    opacity: revealed ? 1 : 0,
                                    overflow: revealed ? 'visible' : 'hidden',
                                    pointerEvents: revealed ? 'auto' : 'none',
                                    transition: 'flex-basis 700ms cubic-bezier(.2,.8,.2,1), width 700ms cubic-bezier(.2,.8,.2,1), opacity 500ms ease-out',
                                }}
                            >
                                <Slider
                                    hideThumbButtons
                                    disabled={!isChecked || disabled}
                                    min={0}
                                    max={100}
                                    value={itemValue}
                                    onChange={(_, value) => {
                                        playDragSound(id + ':' + item.name, itemValue, value as number);
                                        setItemValues(items => (
                                            {
                                                ...items,
                                                [item.name]: {
                                                    ...items[item.name],
                                                    value: value + '',
                                                },
                                            }
                                        ));
                                    }}
                                    onChangeCommitted={(_, value) => onTradeoffValueChange(value, id, item.name)}
                                    sx={{
                                        color: 'var(--app-primary, #2878d4)',
                                        flex: '1 1 0%',
                                        minWidth: 0,
                                        mx: '7px',

                                        '& .MuiSlider-rail': {
                                            opacity: 1,
                                            background: `linear-gradient(
                                                90deg,
                                                #c7d2fe 0%,
                                                var(--app-primary, #2878d4) ${itemValue}%,
                                                #e2e8f0 ${itemValue}%,
                                                #e2e8f0 100%
                                            )`,
                                        },

                                        '& .MuiSlider-track': {
                                            display: 'none',
                                        },

                                        '& .MuiSlider-thumb': {
                                            width: 14,
                                            height: 14,
                                            backgroundColor: 'var(--app-primary, #2878d4)',
                                            border: '2px solid #ffffff',
                                            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)',

                                            '&:before': {
                                                display: 'none',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
}

function getInitialCriteriaFields(items: {
    name: string;
    value: number | string;
    icon: string
}[]) {
    return items.map((item): AppCriteriaField => {
        return {
            name: item.name,
            description: '',
            style: '',
            min: '',
            max: '',
            type: 'select',
            id: 'string' + item.name,
            importance: item.value,
            suffix: '',
            val: '',
            step: '',
            hide_field: false,
            hide_importance: false,
            icon: '',
            images: [],
            technicalField: false,
            calculate: true,
            show_in_result: true,
            suggestedValues: [],
            matching: 'exact',
            viewConfigs: {
                view: '',
            },
            link: '',
        };
    });
}

const AppCriteriaTypeSelectV2 = ({
    importance,
    items,
    hideValues,
    hideImportance,
    onSliderChange,
    onTradeoffValueChange,
    initialValues,
    showAddConstraint,
    setCriteriaConstraints,
    criteriaConstraints,
    id,
    variant = 'Slider',
    isModal = true,
    onCriteriaSettingsOpen = () => {
    },
    disabled = false,
    highlightedOptionNames = [],
    autoExpandedOptionValues,
}: {
    disabled?: boolean;
    importance: number;
    items: {
        name: string;
        value: number | string;
        icon: string;
        link: string;
    }[];
    hideValues?: boolean;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange']
    initialValues: {
        [key: string]: string;
    };
    id: string;
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[];
    };
    setCriteriaConstraints: (value: (((prevState: {
        [p: string]: CriteriaConstraint[];
    }) => {
        [p: string]: CriteriaConstraint[];
    }) | {
                                         [p: string]: CriteriaConstraint[];
                                     })) => void;
    variant?: AppConfigs['criteriaSettings']['enumerated'];
    isModal?: boolean;
    onCriteriaSettingsOpen?: (id: string) => void;
    isInitialRun?: boolean;
    highlightedOptionNames?: string[];
    revealedOptionNames?: string[];
    autoExpandedOptionValues?: Record<string, { from: number; to: number }>;
}) => {
    const [ itemValues, setItemValues ] = useState<{
        [key: string]: {
            isChecked: boolean;
            value: string;
        }
    }>({});
    const [ expandedOptionNames, setExpandedOptionNames ] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                {
                    ...criteriaConstraints,
                    [id]: [],
                }
            ));
        }

        const itemsChecked: {
            [key: string]: {
                isChecked: boolean;
                value: string;
            }
        } = {};
        items.forEach(item => {
            itemsChecked[item.name] = {
                isChecked: item.value !== '-1',
                value: item.value as string,
            };
        });
        setItemValues(itemsChecked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!autoExpandedOptionValues) {
            return;
        }

        const entries = Object.entries(autoExpandedOptionValues);

        if (!entries.length) {
            return;
        }

        setItemValues(prev => {
            const next = { ...prev };

            entries.forEach(([ name, { from } ]) => {
                next[name] = {
                    isChecked: true,
                    value: String(from),
                };
            });

            return next;
        });

        const timeout = setTimeout(() => {
            setItemValues(prev => {
                const next = { ...prev };

                entries.forEach(([ name, { to } ]) => {
                    next[name] = {
                        isChecked: true,
                        value: String(to),
                    };
                });

                return next;
            });
        }, 400);

        return () => clearTimeout(timeout);
    }, [ autoExpandedOptionValues ]);

    const onConstraintChange = (value: string, index: number, key: 'value' | 'rule') => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    [key]: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onConstraintOperatorChange = (value: 'and' | 'or', index: number) => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }

                return {
                    ...constraint,
                    operator: value,
                };
            });

            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onAddConstraint = () => {
        setCriteriaConstraints(constraints => (
            {
                ...constraints,
                [id]: [ ...constraints[id], {
                    value: items[0].name as string || '',
                    rule: '=',
                    operator: constraints[id].length > 0 ? 'and' : null,
                } ],
            }
        ));
    };

    const onDeleteConstraint = (index: number) => {
        setCriteriaConstraints(constraints => {
            const filtered = constraints[id].filter((_, idx) => idx !== index);
            if (!filtered.length) {
                return {
                    ...constraints,
                    [id]: [],
                };
            }

            filtered[0] = {
                ...filtered[0],
                operator: null,
            };

            return {
                ...constraints,
                [id]: filtered,
            };
        });
    };

    const onSliderChangeWrapper = (newValue: number, id: string) => {
        const itemIndex = items.findIndex(item => id === `string${item.name}`);
        if (itemIndex === -1) {
            return;
        }

        onTradeoffValueChange(newValue, id, items[itemIndex].name);
    };

    const styles = !isModal ? {
        transform: 'scale(0.6)',
        width: '100%',
        height: '100%',
    } : {
        width: 1000,
        height: 550,
    };

    const handleSettingsClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isModal) {
            e.preventDefault();
            e.stopPropagation();
            onCriteriaSettingsOpen(id);
        }
    };

    const onExpandOption = (item: SelectItem) => {
        setExpandedOptionNames(new Set(items.map(selectItem => selectItem.name)));
        toggleItemChecked(item, true, setItemValues, onTradeoffValueChange, id, initialValues);
    };

    const isAnyOptionRevealed = expandedOptionNames.size > 0 || highlightedOptionNames.length > 0;
    const slider = renderSelectItems(items, hideImportance || false, itemValues, setItemValues, onTradeoffValueChange, id, initialValues, disabled, highlightedOptionNames, expandedOptionNames, onExpandOption);
    const normalizedVariant = (variant || 'Slider');
    const elements = {
        Default: slider,
        Slider: slider,
        Wheel: slider,
        Swivel: (
            <div
                style={{
                    margin: '0 auto',
                    ...styles,
                }}
                onClick={handleSettingsClick}
            >
                <DraggableDiv
                    initialCriteriaFields={getInitialCriteriaFields(items)}
                    onSliderChange={onSliderChangeWrapper}
                    isEditable={isModal && !disabled}
                />
            </div>
        ),
        Wipe: slider,
        Agent: slider,
    };

    return (
        <div>
            {hideValues ? null : (
                <div style={{ marginBottom: 10 }}>
                    {isAnyOptionRevealed ? (
                        <div className="app-criteria-importance-legend">
                            <span>I don&apos;t prefer</span>
                            <span className="app-criteria-importance-legend-bar"/>
                            <span className="is-high">I prefer</span>
                        </div>
                    ) : (
                        <div className="app-criteria-importance-hint">
                            What&apos;s your preference?
                        </div>
                    )}
                    <div
                        className="multi-select-settings"
                        style={{
                            display: hideImportance ? 'block' : 'flex',
                            minWidth: 250,
                        }}
                    >
                        {elements[normalizedVariant]}
                    </div>
                </div>
            )}
            {hideImportance ? null : (
                <div>
                    <TriangleSlider
                        min={0}
                        max={100}
                        step={1}
                        value={importance}
                        leftText="Not Important"
                        rightText="Really Important"
                        onChange={(_, value) => onSliderChange(value as number, id)}
                        size="medium"
                    />
                </div>
            )}
            {!showAddConstraint ? null : (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: criteriaConstraints[id].length > 0 ? 'block' : 'none' }}>
                        <FormControl sx={{
                            my: 2,
                            minWidth: 120,
                        }} size="small">
                            {criteriaConstraints[id].map((constraint, index) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}
                                    key={id + '_' + index}
                                >
                                    {constraint.operator && (
                                        <Select
                                            style={{ marginRight: 16 }}
                                            variant="outlined"
                                            value={constraint.operator}
                                            onChange={(e) => onConstraintOperatorChange(e.target.value as ('and' | 'or'), index)}
                                        >
                                            <MenuItem value="and">And</MenuItem>
                                            <MenuItem value="or">Or</MenuItem>
                                        </Select>
                                    )}
                                    <Select
                                        variant="outlined"
                                        value={constraint.rule}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'rule')}
                                    >
                                        <MenuItem value="=">Is</MenuItem>
                                        <MenuItem value="!=">Is not</MenuItem>
                                    </Select>
                                    <Select
                                        style={{ marginLeft: 16 }}
                                        value={constraint.value}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'value')}
                                        size="small"
                                        variant="outlined"
                                    >
                                        {items.map(({ name }) => (
                                            <MenuItem value={name} key={name}>{name}</MenuItem>
                                        ))}
                                        <MenuItem value="-">NULL</MenuItem>
                                    </Select>
                                    <IconButton
                                        style={{ marginLeft: '.5rem' }}
                                        onClick={() => onDeleteConstraint(index)}
                                    >
                                        <CloseIcon/>
                                    </IconButton>
                                </div>
                            ))}
                        </FormControl>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        size="small"
                        onClick={onAddConstraint}
                    >
                        Add Constraint
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AppCriteriaTypeSelectV2;
